import styled, { keyframes } from 'styled-components';

import { resetButton, withDecoratedScroll } from 'styles/common';

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(20px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

export const Overlay = styled.div`
  position: fixed;
  bottom: 70px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99;
  animation: ${slideIn} 0.25s ease-out;

  @media (max-width: 768px) {
    bottom: 60px;
  }
`;

export const Panel = styled.div`
  width: calc(100vw - 16px);
  max-width: 600px;
  height: calc(100svh - 260px);
  height: calc(100vh - 260px);
  display: flex;
  flex-direction: column;
  background: rgba(10, 8, 30, 0.95);
  border: 1px solid rgba(155, 89, 182, 0.3);
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(20px);
  overflow: hidden;

  @media (max-width: 768px) {
    width: calc(100vw - 16px);
    max-width: 360px;
    height: calc(100svh - 244px);
    height: calc(100vh - 244px);
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

export const AvatarWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  cursor: pointer;
`;

export const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  border: 1.5px solid rgba(155, 89, 182, 0.5);
  background: #000;

  img {
    object-fit: contain;
    width: 100%;
    height: 100%;
  }
`;

export const OnlineDot = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #039619;
`;

export const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

export const HeaderName = styled.span`
  margin-bottom: 4px;
  font-family: 'Adam', 'Rajdhani', sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.05em;
`;

export const HeaderStatus = styled.span<{ $online?: boolean }>`
  margin-left: 4px;
  font-size: 12px;
  color: ${({ $online }) => ($online ? '#00ff88' : 'rgba(255, 255, 255, 0.4)')};
`;

export const CloseBtn = styled.button`
  ${resetButton}
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  transition: opacity 0.2s;

  svg {
    width: 20px;
    height: 20px;

    path {
      fill: rgba(255, 255, 255, 0.4);
      transition: fill 0.2s;
    }
  }

  &:hover svg path {
    fill: rgba(255, 255, 255, 0.8);
  }
`;

// ── Messages area (wrapper for scroll-down button positioning) ────────────────

export const MessagesArea = styled.div`
  position: relative;
  flex: 1;
  overflow: hidden;
  min-height: 0;
`;

export const Messages = styled.div`
  ${withDecoratedScroll}
  position: absolute;
  inset: 0;
  overflow-y: auto;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const ScrollDownBtn = styled.button`
  ${resetButton}
  position: absolute;
  bottom: 8px;
  right: 14px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(155, 89, 182, 0.5);
  border: 1px solid rgba(155, 89, 182, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  z-index: 1;

  svg {
    width: 14px;
    height: 14px;
  }
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  &:hover {
    background: rgba(155, 89, 182, 0.7);
  }
`;

// ── Message rows ──────────────────────────────────────────────────────────────

export const MessageRow = styled.div<{ $isUser?: boolean }>`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  flex-direction: ${({ $isUser }) => ($isUser ? 'row-reverse' : 'row')};
`;

export const MessageAvatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  margin-top: 2px;
  background: #000;
  cursor: pointer;

  img {
    object-fit: contain;
    width: 100%;
    height: 100%;
  }
`;

export const Bubble = styled.div<{ $isUser?: boolean }>`
  max-width: 75%;
  padding: 8px 12px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.9);
  word-break: break-word;
  white-space: pre-wrap;

  background: ${({ $isUser }) =>
    $isUser ? 'rgba(155, 89, 182, 0.25)' : 'rgba(255, 255, 255, 0.06)'};
  border: 1px solid
    ${({ $isUser }) => ($isUser ? 'rgba(155, 89, 182, 0.3)' : 'rgba(255, 255, 255, 0.08)')};

  border-bottom-right-radius: ${({ $isUser }) => ($isUser ? '4px' : '14px')};
  border-bottom-left-radius: ${({ $isUser }) => ($isUser ? '14px' : '4px')};

  @media (max-width: 600px) {
    font-size: 13px;
  }
`;

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.3; }
  40% { opacity: 1; }
`;

export const TypingDots = styled.span`
  display: inline-flex;
  gap: 3px;

  span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: rgba(155, 89, 182, 0.8);
    animation: ${dotPulse} 1.2s ease-in-out infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
`;

// ── Input area ────────────────────────────────────────────────────────────────

export const InputRow = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

export const TextArea = styled.textarea`
  ${withDecoratedScroll}
  flex: 1;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 18px;
  padding: 7px 14px;
  color: white;
  font-family: 'Rajdhani', sans-serif;
  font-size: 14px;
  line-height: 1.4;
  outline: none;
  resize: none;
  min-height: 36px;
  max-height: 120px;
  overflow-y: auto;
  transition: border-color 0.2s;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    border-color: rgba(155, 89, 182, 0.5);
  }

  &:disabled {
    opacity: 0.5;
  }
`;

export const SendButton = styled.button`
  ${resetButton}
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(155, 89, 182, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
  transition: background 0.2s;

  &:hover {
    background: rgba(155, 89, 182, 0.6);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

// ── Empty state ───────────────────────────────────────────────────────────────

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex: 1;
  color: rgba(255, 255, 255, 0.3);
  font-size: 13px;
  text-align: center;
  padding: 20px;
`;

export const ProfileCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
`;

export const ProfilePhoto = styled.div`
  width: 240px;
  height: 240px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(155, 89, 182, 0.5);
  box-shadow: 0 0 20px rgba(155, 89, 182, 0.3);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ProfileName = styled.h3`
  margin: 0;
  font-family: 'Adam', 'Rajdhani', sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  letter-spacing: 0.08em;
`;

export const ProfileBio = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.55);
  text-align: center;
  max-width: 360px;
`;

export const ProfileTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
`;

export const ProfileTag = styled.span`
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 12px;
  background: rgba(155, 89, 182, 0.15);
  border: 1px solid rgba(155, 89, 182, 0.25);
  color: rgba(200, 160, 230, 0.9);
  letter-spacing: 0.03em;
`;

export const EmptyAvatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(155, 89, 182, 0.3);
  background: #000;
  cursor: pointer;

  img {
    object-fit: contain;
    width: 100%;
    height: 100%;
  }
`;
